import { Spacing } from '@persona-id/design-tokens';
import { LinkExternalIcon } from '@persona-id/design-tokens/icons';
import { every } from 'lodash';
import { omitBy } from 'lodash/fp';
import { useCallback, useEffect, useState } from 'react';
import { FieldPath, FieldPathValue, UseFormSetValue } from 'react-hook-form';
import styled from 'styled-components';

import { InquiryTemplateSimulateFieldsConfigTab } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview';
import {
  INQUIRY_PREVIEW_ID,
  INQUIRY_WIDGET_CONTAINER_ID,
  SIDE_PANEL_CONTAINER_RIGHT_ID,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/InquiryPreview';
import { InquiryTemplateSimulateFormData } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/types';
import {
  allVerificationsWithStatus,
  CHECK_STUBS_SUPPORTED_VERIFICATION_MODELS,
  initialSimulateStubsAndPresets,
  vtmplvIdsTriggeredOnTransition,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { Container } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import VerificationTemplateVersionStubsConfig from 'dashboard/components/Simulate/VerificationTemplateVersionStubsConfig';
import useCreateSimulatePreset from 'dashboard/components/SimulatePreset/hooks/useCreateSimulatePreset';
import useLoadSimulatePresets from 'dashboard/components/SimulatePreset/hooks/useLoadSimulatePresets';
import useUpdateSimulatePreset from 'dashboard/components/SimulatePreset/hooks/useUpdateSimulatePreset';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import {
  CreateSimulatePresetAttributes,
  SimulateCustomPreset,
  SimulatePresetType,
  StubsWithOptionalId,
  UpdateSimulatePresetAttributes,
} from 'dashboard/models/simulate/preset';
import {
  SandboxForcedStatus,
  VerificationTemplateVersionStubs,
} from 'dashboard/models/simulate/stubs';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import hoverableMixin from 'design-system/mixins/hoverable';
import { paragraphMixins } from 'design-system/mixins/text';
import { Hairline } from 'design-system/variables/effect';
import { ApiModel } from 'lib/interfaces/api';

const LargeHeaderContainer = styled.div<{ withBoxShadow?: boolean }>`
  ${paragraphMixins.lgBold};
  display: flex;
  flex: none;
  flex-direction: column;
  padding: ${Spacing[4]} ${Spacing[5]};
  box-shadow: ${({ withBoxShadow }) => (withBoxShadow ? Hairline.Bottom : 'none')};
`;

const PassFailConfigContainer = styled('div')`
  padding: ${Spacing[2]} ${Spacing[5]} ${Spacing[2]} ${Spacing[5]};
  flex: auto;
  overflow-y: auto;
  margin-top: 1px;
`;

const RadioGroupContainer = styled.div`
  margin-top: ${Spacing[2]};
`;

const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleSliderContainer = styled.div`
  float: right;
  display: flex;
  align-items: center;
`;

const StyledToggleSlider = styled(ToggleSlider)`
  margin-right: ${Spacing[2]};
  height: fit-content;
`;

const StyledTabBar = styled(TabBar)`
  padding-left: ${Spacing[5]};
`;

const StyledAccordionText = styled.div`
  ${paragraphMixins.lgBold}
`;

const StyledMessage = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  padding: ${Spacing[3]} ${Spacing[5]} ${Spacing[3]} ${Spacing[5]};
  color: ${({ theme }) => theme.color.text.secondary};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
`;

const StyledLabel = styled(Label)`
  display: inline-block;
  font-weight: ${({ theme }) => theme.text.paragraph.mdBold.fontWeight};
  margin-bottom: 0px;
`;

const FieldsConfigContainer = styled.div`
  padding: ${Spacing[2]} ${Spacing[5]} ${Spacing[2]} ${Spacing[5]};
  overflow: auto;
  height: 100%;
  flex: 1;
`;

const VerificationsConfigContainer = styled.div<{ hidden?: boolean }>`
  display: ${({ hidden }) => (hidden ? 'none' : 'block')};
  overflow: auto;
  height: 100%;
  flex: 1;
`;

const VerificationStubAccordionContainer = styled.div`
  padding: 0 ${Spacing[5]} 0 ${Spacing[5]};

  > ${FormGroup} {
    margin-bottom: ${Spacing[2]};
  }
`;

// Specifically we are not using a Button here because icons in buttons don't play well
// with RTL
const StyledLinkExternalIcon = styled(LinkExternalIcon)`
  ${hoverableMixin};
  &:hover {
    background: ${({ theme }) => theme.color.primary.white};
  }
  border-radius: ${({ theme }) => theme.button.borderRadius};
  width: 25px;
  height: 25px;
  padding: 4px;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
`;

// An entire VerificationTemplateVersionStub or a subset therein
type VerificationTemplateVersionPartialStub =
  | FieldPathValue<VerificationTemplateVersionStubs, FieldPath<VerificationTemplateVersionStubs>>
  | VerificationTemplateVersionStubs;

const isSimpleModeStubs = (formData: InquiryTemplateSimulateFormData): boolean => {
  return formData['verification-template-version-stubs']
    .flatMap((vtvStubs) => vtvStubs['verification-stubs'])
    .every((vStubs) => 'statusStub' in vStubs);
};

const getSimulateBannerMessage = ({
  shownVtvTypes,
  isAdvancedMode,
}: {
  shownVtvTypes: ApiModel[];
  isAdvancedMode: boolean;
}): string | null => {
  if (shownVtvTypes.length === 0) {
    return null;
  }

  let message = 'Real verifications are not performed in Preview.';

  if (
    !isAdvancedMode ||
    shownVtvTypes.every(
      (vtvType: ApiModel) => !CHECK_STUBS_SUPPORTED_VERIFICATION_MODELS.includes(vtvType),
    )
  ) {
    // Simple mode, or Advanced mode but all the current verifications only have pass/fail support
    message += ' Use Simulate controls to specify the verification to pass or fail.';
  } else {
    if (shownVtvTypes.includes(ApiModel.VerificationTemplateVersionGovernmentId)) {
      // Advanced mode and at least one verification has extractions stubbing
      message +=
        ' Use Simulate controls to specify simulated ID details and verification check results';
    } else {
      // Advanced mode and no verifications have extractions stubbing
      message += ' Use Simulate controls to specify simulated verification check results';
    }

    if (
      shownVtvTypes.some(
        (vtvType: ApiModel) => !CHECK_STUBS_SUPPORTED_VERIFICATION_MODELS.includes(vtvType),
      )
    ) {
      // Advanced mode and at least one verification has only pass/fail support and at
      // least one verification has checks stubbing support (rare case)
      message += ', or specify the verification to pass or fail.';
    } else {
      message += '.';
    }
  }

  return message + ' All inputted, uploaded, and captured data via this screen are ignored.';
};

interface VerificationTemplateVersionLinkProps {
  inquiryTemplateId: string;
  inquiryTemplateVersionId: string;
  verificationTemplateVersion: VerificationTemplateVersion;
}

const VerificationTemplateVersionLink = ({
  inquiryTemplateId,
  inquiryTemplateVersionId,
  verificationTemplateVersion,
}: VerificationTemplateVersionLinkProps) => {
  return (
    <FlexContainer>
      <StyledAccordionText>
        {verificationTemplateVersion.attributes['template-name'] ||
          `${
            VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[verificationTemplateVersion.type]
          } verification`}
      </StyledAccordionText>
      <StyledLinkExternalIcon
        onClick={(e) => {
          // So that the parent accordion does not record a click
          e.stopPropagation();
          window.open(
            `/dashboard/inquiry-templates/${inquiryTemplateId}/${inquiryTemplateVersionId}/config/verifications/${verificationTemplateVersion.id}`,
            '_blank',
          );
        }}
      />
    </FlexContainer>
  );
};

export interface VerificationTemplateVersionStubAccordionProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  verificationTemplateVersion: VerificationTemplateVersion;
  verificationTemplateVersionStubs: VerificationTemplateVersionStubs;
  handleCreatePreset: (attrs: CreateSimulatePresetAttributes) => Promise<SimulateCustomPreset>;
  handleUpdatePreset: (
    id: string,
    type: SimulatePresetType,
    props: UpdateSimulatePresetAttributes,
  ) => Promise<SimulateCustomPreset>;
  customPresets: SimulateCustomPreset[];
  loadingSimulatePresets: boolean;
  reloadPresets: () => Promise<void>;
  selectedPreset: StubsWithOptionalId | null;
  setSelectedPreset: (preset: StubsWithOptionalId | null) => void;
  setVerificationTemplateVersionStubsFieldPath: (
    stubFieldPath: FieldPath<VerificationTemplateVersionStubs> | null,
    stub: VerificationTemplateVersionPartialStub,
  ) => void;
  defaultExpanded: boolean;
  removeCustomPreset: (preset: StubsWithOptionalId) => void;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}

const VerificationTemplateVersionStubAccordion = ({
  verificationTemplateVersion,
  verificationTemplateVersionStubs,
  inquiryTemplateVersion,
  handleCreatePreset,
  handleUpdatePreset,
  defaultExpanded,
  customPresets,
  loadingSimulatePresets,
  reloadPresets,
  selectedPreset,
  setSelectedPreset,
  setVerificationTemplateVersionStubsFieldPath,
  removeCustomPreset,
  trackEvent,
}: VerificationTemplateVersionStubAccordionProps) => {
  return (
    <SectionAccordion
      key={`config-${verificationTemplateVersion.id}`}
      title={
        <VerificationTemplateVersionLink
          verificationTemplateVersion={verificationTemplateVersion}
          inquiryTemplateId={inquiryTemplateVersion.relationships['inquiry-template'].data.id}
          inquiryTemplateVersionId={inquiryTemplateVersion.id}
        />
      }
      fullWidth={true}
      defaultExpanded={defaultExpanded}
    >
      <VerificationStubAccordionContainer>
        <VerificationTemplateVersionStubsConfig
          verificationTemplateVersion={verificationTemplateVersion}
          verificationTemplateVersionStubs={verificationTemplateVersionStubs}
          setVerificationTemplateVersionStubsFieldPath={
            setVerificationTemplateVersionStubsFieldPath
          }
          customPresets={customPresets}
          handleUpdatePreset={handleUpdatePreset}
          removeCustomPreset={removeCustomPreset}
          loadingSimulatePresets={loadingSimulatePresets}
          reloadPresets={reloadPresets}
          selectedPreset={selectedPreset}
          handleCreatePreset={handleCreatePreset}
          setSelectedPreset={setSelectedPreset}
          panelPortalId={INQUIRY_PREVIEW_ID}
          offsetElementId={SIDE_PANEL_CONTAINER_RIGHT_ID}
          panelOverlayId={INQUIRY_WIDGET_CONTAINER_ID}
          trackEvent={trackEvent}
        />
      </VerificationStubAccordionContainer>
    </SectionAccordion>
  );
};

export interface VerificationsConfigTabProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  verificationTemplateVersionStubsArray: VerificationTemplateVersionStubs[];
  handleCreatePreset: (attrs: CreateSimulatePresetAttributes) => Promise<SimulateCustomPreset>;
  handleUpdatePreset: (
    id: string,
    type: SimulatePresetType,
    props: UpdateSimulatePresetAttributes,
  ) => Promise<SimulateCustomPreset>;
  customPresets: SimulateCustomPreset[];
  loadingSimulatePresets: boolean;
  reloadPresets: () => Promise<void>;
  vtvToSelectedPreset: Record<string, StubsWithOptionalId | null>;
  setPresetForVtv: (vtvId: string, preset: StubsWithOptionalId | null) => void;
  setVerificationTemplateVersionStubsFieldPath: (
    vtvStubIndex: number,
    vtvStubFieldPath: FieldPath<VerificationTemplateVersionStubs> | null,
    stub: VerificationTemplateVersionPartialStub,
  ) => void;
  removeCustomPreset: (preset: StubsWithOptionalId) => void;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
  hidden: boolean;
}

const VerificationsConfigTab = ({
  inquiryTemplateVersion,
  verificationTemplateVersions,
  verificationTemplateVersionStubsArray,
  handleCreatePreset,
  handleUpdatePreset,
  customPresets,
  loadingSimulatePresets,
  reloadPresets,
  vtvToSelectedPreset,
  setPresetForVtv,
  setVerificationTemplateVersionStubsFieldPath,
  removeCustomPreset,
  trackEvent,
  hidden,
}: VerificationsConfigTabProps) => {
  if (Object.keys(verificationTemplateVersions).length === 0) {
    return (
      <VerificationsConfigContainer hidden={hidden}>
        <StyledMessage>No verifications in this Inquiry.</StyledMessage>
      </VerificationsConfigContainer>
    );
  }

  return (
    <VerificationsConfigContainer hidden={hidden}>
      <>
        {verificationTemplateVersionStubsArray.map(
          (verificationTemplateVersionStub, vtvStubIndex) => {
            const verificationTemplateVersionId = verificationTemplateVersionStub.id;
            const verificationTemplateVersion =
              verificationTemplateVersions[verificationTemplateVersionId];

            return (
              <div key={vtvStubIndex} role='listitem'>
                <VerificationTemplateVersionStubAccordion
                  verificationTemplateVersion={verificationTemplateVersion}
                  verificationTemplateVersionStubs={verificationTemplateVersionStub}
                  inquiryTemplateVersion={inquiryTemplateVersion}
                  handleCreatePreset={handleCreatePreset}
                  defaultExpanded={false}
                  customPresets={customPresets}
                  loadingSimulatePresets={loadingSimulatePresets}
                  handleUpdatePreset={handleUpdatePreset}
                  removeCustomPreset={removeCustomPreset}
                  selectedPreset={vtvToSelectedPreset[verificationTemplateVersionId]}
                  setSelectedPreset={(preset) =>
                    setPresetForVtv(verificationTemplateVersionId, preset)
                  }
                  setVerificationTemplateVersionStubsFieldPath={(stubFieldPath, stub) =>
                    setVerificationTemplateVersionStubsFieldPath(vtvStubIndex, stubFieldPath, stub)
                  }
                  reloadPresets={reloadPresets}
                  trackEvent={trackEvent}
                />
              </div>
            );
          },
        )}
      </>
    </VerificationsConfigContainer>
  );
};

export interface VerificationsPassFailConfigProps {
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  setAllVerificationsWithStatus: (status: SandboxForcedStatus) => void;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}

const VerificationsPassFailConfig = ({
  setAllVerificationsWithStatus,
  trackEvent,
}: VerificationsPassFailConfigProps) => {
  const [verificationsStatus, setVerificationsStatus] = useState(SandboxForcedStatus.Passed);

  const setStatusStubs = useCallback(
    (status: SandboxForcedStatus) => {
      trackEvent('Flow Editor - Preview Simple Mode Pass/Fail Toggled');
      setVerificationsStatus(status);
      setAllVerificationsWithStatus(status);
    },
    [trackEvent, setAllVerificationsWithStatus],
  );

  return (
    <PassFailConfigContainer>
      <StyledLabel htmlFor='pass-fail-config-input' size='md'>
        Verifications
      </StyledLabel>
      <RadioGroupContainer>
        <RadioGroup id='pass-fail-config' onChange={setStatusStubs} value={verificationsStatus}>
          <Radio key='verifications-pass' label='Pass' value={SandboxForcedStatus.Passed} />
          <Radio key='verifications-fail' label='Fail' value={SandboxForcedStatus.Failed} />
        </RadioGroup>
      </RadioGroupContainer>
    </PassFailConfigContainer>
  );
};

interface CurrentStepConfigTabProps {
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  verificationTemplateVersionStubsArray: VerificationTemplateVersionStubs[];
  inquiryTemplateVersion: InquiryTemplateVersion;
  shownVtvIds: string[];
  hidden: boolean;
  customPresets: SimulateCustomPreset[];
  loadingSimulatePresets: boolean;
  reloadPresets: () => Promise<void>;
  removeCustomPreset: (preset: StubsWithOptionalId) => void;
  handleCreatePreset: (attrs: CreateSimulatePresetAttributes) => Promise<SimulateCustomPreset>;
  handleUpdatePreset: (
    id: string,
    type: SimulatePresetType,
    props: UpdateSimulatePresetAttributes,
  ) => Promise<SimulateCustomPreset>;
  vtvToSelectedPreset: Record<string, StubsWithOptionalId | null>;
  setPresetForVtv: (vtvId: string, preset: StubsWithOptionalId | null) => void;
  setVerificationTemplateVersionStubsFieldPath: (
    vtvStubIndex: number,
    vtvStubFieldPath: FieldPath<VerificationTemplateVersionStubs> | null,
    stub: VerificationTemplateVersionPartialStub,
  ) => void;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}

const CurrentStepConfigTab = ({
  verificationTemplateVersions,
  verificationTemplateVersionStubsArray,
  inquiryTemplateVersion,
  shownVtvIds,
  hidden,
  handleCreatePreset,
  handleUpdatePreset,
  customPresets,
  loadingSimulatePresets,
  reloadPresets,
  vtvToSelectedPreset,
  setPresetForVtv,
  setVerificationTemplateVersionStubsFieldPath,
  removeCustomPreset,
  trackEvent,
}: CurrentStepConfigTabProps) => {
  if (every([shownVtvIds], (arr) => arr.length === 0)) {
    return (
      <div style={{ display: hidden ? 'none' : 'block' }}>
        <StyledMessage>Nothing to simulate on this step.</StyledMessage>
      </div>
    );
  }

  return (
    <div style={{ display: hidden ? 'none' : 'block' }}>
      <>
        {verificationTemplateVersionStubsArray.map(
          (verificationTemplateVersionStub, vtvStubIndex) => {
            const verificationTemplateVersionId = verificationTemplateVersionStub.id;
            const verificationTemplateVersion =
              verificationTemplateVersions[verificationTemplateVersionId];

            if (shownVtvIds != null && !shownVtvIds.includes(verificationTemplateVersionId)) {
              return <></>;
            }

            return (
              <div key={vtvStubIndex} role='listitem'>
                <VerificationTemplateVersionStubAccordion
                  verificationTemplateVersion={verificationTemplateVersion}
                  verificationTemplateVersionStubs={verificationTemplateVersionStub}
                  inquiryTemplateVersion={inquiryTemplateVersion}
                  handleCreatePreset={handleCreatePreset}
                  handleUpdatePreset={handleUpdatePreset}
                  removeCustomPreset={removeCustomPreset}
                  customPresets={customPresets}
                  loadingSimulatePresets={loadingSimulatePresets}
                  reloadPresets={reloadPresets}
                  selectedPreset={vtvToSelectedPreset[verificationTemplateVersionId]}
                  setSelectedPreset={(preset) =>
                    setPresetForVtv(verificationTemplateVersionId, preset)
                  }
                  setVerificationTemplateVersionStubsFieldPath={(stubFieldPath, stub) =>
                    setVerificationTemplateVersionStubsFieldPath(vtvStubIndex, stubFieldPath, stub)
                  }
                  defaultExpanded={true}
                  trackEvent={trackEvent}
                />
              </div>
            );
          },
        )}
      </>
    </div>
  );
};

interface InquiryTemplateSimulateStubbingProps {
  inquiry: Inquiry | undefined;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  inquiryTemplateVersion: InquiryTemplateVersion;
  workflowVersionMap: WorkflowVersionMap;
  currentStepName: string;
  setSimulateBannerMessage: (message: string | null) => void;
  numTransitionPageChanges: number;
  simulateStubsFormValues: InquiryTemplateSimulateFormData;
  setSimulateStubsFormValues: UseFormSetValue<InquiryTemplateSimulateFormData>;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
  isAdvancedMode: boolean;
  toggleAdvancedMode: () => void;
  isLoading: boolean;
}

export const InquiryTemplateSimulateStubbing = ({
  inquiry,
  verificationTemplateVersions,
  inquiryTemplateVersion,
  workflowVersionMap,
  currentStepName,
  numTransitionPageChanges,
  simulateStubsFormValues,
  setSimulateStubsFormValues,
  setSimulateBannerMessage,
  trackEvent,
  isAdvancedMode,
  toggleAdvancedMode,
  isLoading,
}: InquiryTemplateSimulateStubbingProps) => {
  useEffect(() => {
    trackEvent('Flow Editor - Preview Page Change', {
      simple_mode: isSimpleModeStubs(simulateStubsFormValues),
    });
  }, [numTransitionPageChanges]);

  const [selectedTabIdx, setSelectedTabIdx] = useState(0);

  // Assign a preset to a specific VTV
  const setPresetForVtv = (vtvId: string, preset: StubsWithOptionalId | null) =>
    setSimulateStubsFormValues(`vtv-to-selected-preset.${vtvId}`, preset);

  // Remove a preset for all VTVs. Intended to be used when deleting a preset and removing it from use.
  const removeCustomPreset = (removedPreset: StubsWithOptionalId) =>
    setSimulateStubsFormValues(
      'vtv-to-selected-preset',
      omitBy(
        (preset) => preset?.id === removedPreset.id,
        simulateStubsFormValues['vtv-to-selected-preset'],
      ),
    );

  const setVerificationTemplateVersionStubsFieldPath = (
    vtvStubIndex: number,
    vtvStubFieldPath: FieldPath<VerificationTemplateVersionStubs> | null,
    stub: VerificationTemplateVersionPartialStub,
  ) =>
    setSimulateStubsFormValues(
      vtvStubFieldPath
        ? `verification-template-version-stubs.${vtvStubIndex}.${vtvStubFieldPath}`
        : `verification-template-version-stubs.${vtvStubIndex}`,
      stub,
    );

  const { handleLoadSimulatePresets, loadingSimulatePresets } = useLoadSimulatePresets();
  const { handleCreateSimulatePreset } = useCreateSimulatePreset();
  const { handleUpdateSimulatePreset } = useUpdateSimulatePreset();

  const reloadPresets = useCallback(async () => {
    setCustomPresets(
      await handleLoadSimulatePresets({
        filter: { type: [ApiModel.SimulatePresetVerification] },
        sortParams: { sort: 'name', shouldSortDescending: false },
      }),
    );
  }, [handleLoadSimulatePresets]);

  const handleCreatePresetAndReload = useCallback(
    async (props: CreateSimulatePresetAttributes) => {
      const preset = await handleCreateSimulatePreset(props);
      reloadPresets();
      return preset;
    },
    [handleCreateSimulatePreset, reloadPresets],
  );

  const handleUpdatePresetAndReload = useCallback(
    async (id: string, type: SimulatePresetType, props: UpdateSimulatePresetAttributes) => {
      const preset = await handleUpdateSimulatePreset(id, type, props);
      reloadPresets();
      return preset;
    },
    [handleUpdateSimulatePreset, reloadPresets],
  );

  // FIXME: Implement as part of `useLoadSimulatePresets`
  const [customPresets, setCustomPresets] = useState<SimulateCustomPreset[]>([]);

  useEffect(() => {
    handleLoadSimulatePresets({
      filter: { type: [ApiModel.SimulatePresetVerification] },
      sortParams: { sort: 'name', shouldSortDescending: false },
    }).then((simulatePresets) => {
      setCustomPresets(simulatePresets);
    });
  }, [handleLoadSimulatePresets]);

  const currentStep = inquiryTemplateVersion.attributes.steps.find(
    (step) => step.name === currentStepName,
  );
  const shownVtvIds = vtmplvIdsTriggeredOnTransition({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    step: currentStep,
    workflowVersionMap,
  });
  const shownVtvTypes = shownVtvIds.map((vtvId) => verificationTemplateVersions[vtvId].type);

  // Execute this effect when the the `currentStepName` changes because that is when
  // we need to evaluate whether or not to show a banner.
  useEffect(() => {
    setSimulateBannerMessage(getSimulateBannerMessage({ shownVtvTypes, isAdvancedMode }));
  }, [isAdvancedMode, currentStepName, setSimulateBannerMessage]);

  const tabsConfig = [
    {
      name: 'Current Step',
      contents: (
        <CurrentStepConfigTab
          verificationTemplateVersions={verificationTemplateVersions}
          verificationTemplateVersionStubsArray={
            simulateStubsFormValues['verification-template-version-stubs']
          }
          inquiryTemplateVersion={inquiryTemplateVersion}
          shownVtvIds={shownVtvIds}
          hidden={selectedTabIdx !== 0}
          trackEvent={trackEvent}
          handleCreatePreset={handleCreatePresetAndReload}
          handleUpdatePreset={handleUpdatePresetAndReload}
          customPresets={customPresets}
          loadingSimulatePresets={loadingSimulatePresets}
          vtvToSelectedPreset={simulateStubsFormValues['vtv-to-selected-preset']}
          setPresetForVtv={setPresetForVtv}
          setVerificationTemplateVersionStubsFieldPath={
            setVerificationTemplateVersionStubsFieldPath
          }
          removeCustomPreset={removeCustomPreset}
          reloadPresets={reloadPresets}
        />
      ),
    },
    {
      name: 'All Verifications',
      contents: (
        <VerificationsConfigTab
          verificationTemplateVersions={verificationTemplateVersions}
          verificationTemplateVersionStubsArray={
            simulateStubsFormValues['verification-template-version-stubs']
          }
          inquiryTemplateVersion={inquiryTemplateVersion}
          trackEvent={trackEvent}
          handleCreatePreset={handleCreatePresetAndReload}
          handleUpdatePreset={handleUpdatePresetAndReload}
          customPresets={customPresets}
          loadingSimulatePresets={loadingSimulatePresets}
          vtvToSelectedPreset={simulateStubsFormValues['vtv-to-selected-preset']}
          setPresetForVtv={setPresetForVtv}
          setVerificationTemplateVersionStubsFieldPath={
            setVerificationTemplateVersionStubsFieldPath
          }
          removeCustomPreset={removeCustomPreset}
          reloadPresets={reloadPresets}
          hidden={selectedTabIdx !== 1}
        />
      ),
    },
    {
      name: 'Inquiry Fields',
      contents: (
        <FieldsConfigContainer>
          <InquiryTemplateSimulateFieldsConfigTab
            inquiry={inquiry}
            fieldSchemas={inquiryTemplateVersion.attributes['field-schema']}
            fieldsStubs={simulateStubsFormValues['fields-stubs']}
            setFieldsStubs={(stubs) => setSimulateStubsFormValues('fields-stubs', stubs)}
          />
        </FieldsConfigContainer>
      ),
    },
  ];

  return (
    <Container aria-label='Simulate stubbing' width='450px'>
      <LargeHeaderContainer withBoxShadow={!isAdvancedMode}>
        <TitleContainer>
          Simulate
          <ToggleSliderContainer>
            <StyledToggleSlider
              id='simulate-toggle-mode'
              labelOn='Advanced'
              labelOff='Advanced'
              value={isAdvancedMode}
              onChange={(isAdvancedMode) => {
                const stubsAndPresets = initialSimulateStubsAndPresets(
                  verificationTemplateVersions,
                  {
                    isAdvancedMode,
                  },
                );
                Object.entries(stubsAndPresets).forEach(([fieldName, fieldValue]) => {
                  setSimulateStubsFormValues(
                    fieldName as keyof InquiryTemplateSimulateFormData,
                    fieldValue,
                  );
                });
                toggleAdvancedMode();
              }}
            />
          </ToggleSliderContainer>
        </TitleContainer>
        <Spacer height={2} />
        <Paragraph>
          {`To view different paths through the flow, set verifications to pass or fail. Use
            advanced mode to simulate outcomes for specific verification checks and fields.`}
        </Paragraph>
      </LargeHeaderContainer>
      {!isLoading && (
        <>
          {isAdvancedMode && (
            <StyledTabBar>
              {tabsConfig.map((tabConfig, idx) => (
                <Tab
                  id={`tab-${tabConfig.name}`}
                  key={`tab-${tabConfig.name}`}
                  selected={selectedTabIdx === idx}
                  onClick={() => setSelectedTabIdx(idx)}
                >
                  {tabConfig.name}
                </Tab>
              ))}
            </StyledTabBar>
          )}
          {isAdvancedMode ? (
            <div style={{ overflow: 'auto', height: '100%' }}>
              {/*This is a little hack to allow us to persist the accordion state in the */}
              {/*"Current Step" (0th) and "All Verifications" (1st) tab as we click through */}
              {/*different tabs. Because the tab is never unmounted when navigating to a */}
              {/*different tab, but hidden instead, the accordion will remain open if it was previously open */}
              {tabsConfig[0].contents}
              {tabsConfig[1].contents}
              {selectedTabIdx !== 0 && selectedTabIdx !== 1 && tabsConfig[selectedTabIdx].contents}
            </div>
          ) : (
            <VerificationsPassFailConfig
              verificationTemplateVersions={verificationTemplateVersions}
              setAllVerificationsWithStatus={(status) =>
                setSimulateStubsFormValues(
                  'verification-template-version-stubs',
                  allVerificationsWithStatus(verificationTemplateVersions, status),
                )
              }
              trackEvent={trackEvent}
            />
          )}
        </>
      )}
    </Container>
  );
};
